
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

interface CookieConsentBannerProps {
  onPreferencesOpen?: () => void;
}

export default function CookieConsentBanner({ onPreferencesOpen }: CookieConsentBannerProps) {
  const [showBanner, setShowBanner] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const cookiePreferences = localStorage.getItem('cookiePreferences');
    const hasConsented = localStorage.getItem('cookieConsent');
    
    if (!cookiePreferences && !hasConsented) {
      // Delay showing banner slightly for better UX
      setTimeout(() => {
        setShowBanner(true);
        setTimeout(() => setIsVisible(true), 100);
      }, 1000);
    }
  }, []);

  // Close banner when pressing Escape key
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && showBanner) {
        hideBanner();
      }
    };

    if (showBanner) {
      document.addEventListener('keydown', handleEscapeKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [showBanner]);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
    };
    
    localStorage.setItem('cookiePreferences', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsent', 'accepted');
    hideBanner();
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      marketing: false,
    };
    
    localStorage.setItem('cookiePreferences', JSON.stringify(essentialOnly));
    localStorage.setItem('cookieConsent', 'partial');
    hideBanner();
  };

  const handleManagePreferences = () => {
    if (onPreferencesOpen) {
      onPreferencesOpen();
    } else {
      // Navigate to cookie policy page
      window.location.href = '/cookie-policy';
    }
    hideBanner();
  };

  const hideBanner = () => {
    setIsVisible(false);
    setTimeout(() => setShowBanner(false), 300);
  };

  if (!showBanner) return null;

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-[9999] transition-all duration-300 transform ${
        isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'
      }`}
      role="dialog"
      aria-modal="true"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-description"
    >
      {/* Backdrop blur */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm"></div>
      
      {/* Banner content */}
      <div className="relative bg-gradient-to-r from-[#0a0a0a]/95 via-[#1a1a1a]/95 to-[#0a0a0a]/95 backdrop-blur-md border-t border-white/10">
        {/* Subtle neon glow effect */}
        <div className="absolute inset-0 bg-gradient-to-r from-[#8b2bd9]/5 via-transparent to-[#3b82f6]/5 pointer-events-none"></div>
        
        <div className="relative max-w-[1400px] mx-auto p-4 md:p-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-4 lg:gap-6">
            
            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-3 mb-3">
                <div className="flex-shrink-0 w-5 h-5 mt-0.5" aria-hidden="true">
                  <i className="ri-shield-check-line text-[#8b2bd9] text-lg"></i>
                </div>
                <div>
                  <h3 id="cookie-banner-title" className="text-base md:text-lg font-semibold text-white mb-1">
                    Cookie Preferences
                  </h3>
                  <p id="cookie-banner-description" className="text-sm text-white/80 leading-relaxed">
                    We use cookies to enhance your experience, analyse site traffic, and provide personalised content. 
                    Essential cookies are always active to ensure the site functions properly.{' '}
                    <Link 
                      to="/cookie-policy" 
                      className="text-[#8b2bd9] hover:text-[#3b82f6] transition-colors underline decoration-dotted focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black rounded"
                      aria-label="Learn more about our cookie policy"
                    >
                      Learn more
                    </Link>
                  </p>
                </div>
              </div>
            </div>

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto lg:flex-shrink-0" role="group" aria-label="Cookie consent options">
              <button
                onClick={handleRejectNonEssential}
                className="h-12 px-4 bg-white/10 hover:bg-white/20 focus:bg-white/20 text-white text-sm font-medium rounded-lg border border-white/20 hover:border-white/40 focus:border-white/40 transition-all cursor-pointer whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black"
                aria-label="Accept only essential cookies"
              >
                Essential Only
              </button>
              
              <button
                onClick={handleManagePreferences}
                className="h-12 px-4 bg-gradient-to-r from-[#8b2bd9]/20 to-[#3b82f6]/20 hover:from-[#8b2bd9]/30 hover:to-[#3b82f6]/30 focus:from-[#8b2bd9]/30 focus:to-[#3b82f6]/30 text-white text-sm font-medium rounded-lg border border-[#8b2bd9]/30 hover:border-[#8b2bd9]/50 focus:border-[#8b2bd9]/50 transition-all cursor-pointer whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 focus:ring-offset-black"
                aria-label="Manage cookie preferences"
              >
                Manage Preferences
              </button>
              
              <button
                onClick={handleAcceptAll}
                className="h-12 px-4 bg-gradient-to-r from-[#8b2bd9] to-[#3b82f6] hover:from-[#7a26c1] hover:to-[#2563eb] focus:from-[#7a26c1] focus:to-[#2563eb] text-white text-sm font-semibold rounded-lg shadow-lg hover:shadow-[#8b2bd9]/25 focus:shadow-[#8b2bd9]/25 transition-all cursor-pointer whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-purple-300 focus:ring-offset-2 focus:ring-offset-black"
                aria-label="Accept all cookies"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>

        {/* Subtle bottom glow */}
        <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#8b2bd9]/50 to-transparent"></div>
      </div>
    </div>
  );
}
